import React from "react";
import "@/App.css";
import axios from 'axios';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ProjectProvider } from "./context/ProjectContext";
import { ChartPreferencesProvider } from "./context/ChartPreferencesContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { HomePage } from "./components/HomePage";
import { DashboardPage } from "./components/DashboardPage";
import { SpecificationsPage } from "./components/SpecificationsPage";
import { InvestmentPage } from "./components/InvestmentPage";
import { FinancingPage } from "./components/FinancingPage";
import { SalesVolumePage } from "./components/SalesVolumePage";
import { PricingPage } from "./components/PricingPage";
import { FixedExpensesPage } from "./components/FixedExpensesPage";
import { IncomeStatementPage } from "./components/IncomeStatementPage";
import { CashFlowPage } from "./components/CashFlowPage";
import { ValuationPage } from "./components/ValuationPage";
import { ScenariosPage } from "./components/ScenariosPage";
import { ProjectExplainedPage } from "./components/ProjectExplainedPage";
import { SettingsPage } from "./components/SettingsPage";
import LandingPage from "./components/LandingPage";
import LoginPage from "./components/LoginPage";
import AuthCallback from "./components/AuthCallback";
import PaymentSuccess from "./components/PaymentSuccess";
import PricingPage2 from "./components/PricingPage2";
import AIAnalystPage from "./components/AIAnalystPage";
import AIModifyPage from "./components/AIModifyPage";
import AdminPage from "./components/AdminPage";
import PremiumRoute from "./components/PremiumRoute";

// Global axios interceptor: auto-attach auth token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Detect session_id during render (NOT in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pricing" element={<PricingPage2 />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      
      {/* Protected app routes */}
      <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<HomePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="ai-analyst" element={<AIAnalystPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="project/:projectId/ai-modify" element={<PremiumRoute title="Modificar con IA"><AIModifyPage /></PremiumRoute>} />
        <Route path="project/:projectId/dashboard" element={<DashboardPage />} />
        <Route path="project/:projectId/specifications" element={<SpecificationsPage />} />
        <Route path="project/:projectId/investment" element={<InvestmentPage />} />
        <Route path="project/:projectId/financing" element={<FinancingPage />} />
        <Route path="project/:projectId/sales-volume" element={<SalesVolumePage />} />
        <Route path="project/:projectId/pricing" element={<PricingPage />} />
        <Route path="project/:projectId/fixed-expenses" element={<FixedExpensesPage />} />
        <Route path="project/:projectId/income-statement" element={<PremiumRoute title="Estado de Resultados"><IncomeStatementPage /></PremiumRoute>} />
        <Route path="project/:projectId/cash-flow" element={<PremiumRoute title="Flujo de Efectivo"><CashFlowPage /></PremiumRoute>} />
        <Route path="project/:projectId/valuation" element={<PremiumRoute title="Valuacion del Proyecto"><ValuationPage /></PremiumRoute>} />
        <Route path="project/:projectId/scenarios" element={<PremiumRoute title="Escenarios y Exportacion"><ScenariosPage /></PremiumRoute>} />
        <Route path="project/:projectId/explained" element={<PremiumRoute title="Tu Proyecto Explicado"><ProjectExplainedPage /></PremiumRoute>} />
      </Route>
    </Routes>
  );
}

import { UnsavedChangesProvider } from "./components/UnsavedChangesDialog";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ChartPreferencesProvider>
          <ProjectProvider>
            <BrowserRouter>
              <UnsavedChangesProvider>
                <AppRouter />
              </UnsavedChangesProvider>
            </BrowserRouter>
          </ProjectProvider>
        </ChartPreferencesProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
